import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchServices, type Service } from '@/api/client';
import { services as mockServices } from '@/mocks/services';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function ServicesSection() {
  const { t } = useTranslation('home');
  const [serviceList, setServiceList] = useState<Service[]>(mockServices);

  useEffect(() => {
    let cancelled = false;
    fetchServices().then((data) => {
      if (cancelled) return;
      if (data.length === 0) {
        setServiceList(mockServices);
        return;
      }
      // fetchServices() already maps: short_description→description, image_url→image
      const merged = data.map((apiService, i) => {
        const fallback = mockServices[i] || mockServices[0];
        return {
          ...fallback,
          id: apiService.id,
          title: apiService.title || fallback.title,
          description: apiService.description || fallback.description,
          icon: apiService.icon || fallback.icon,
          image: apiService.image?.trim() || fallback.image,
        };
      });
      setServiceList(merged);
    }).catch(() => {
      if (!cancelled) setServiceList(mockServices);
    });
    return () => { cancelled = true; };
  }, []);

  const [activeCard, setActiveCard] = useState<number | null>(null);

  return (
    <section id="services" className="w-full py-16 md:py-20 bg-brand-light">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-mint" />
            <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
              {t('services_label')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
            {t('services_title')}
          </h2>
          <div className="w-10 h-1 bg-brand-mint rounded-full" />
        </ScrollReveal>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {serviceList.map((service, idx) => (
            <ScrollReveal key={service.id} delay={idx * 80}>
              <div
                className={`group relative bg-white rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
                  activeCard === idx
                    ? 'border-t-[3px] border-brand-mint shadow-lg'
                    : 'border-t-[3px] border-transparent shadow-sm hover:shadow-md'
                }`}
                onMouseEnter={() => setActiveCard(idx)}
                onMouseLeave={() => setActiveCard(null)}
              >
                {/* Service Image */}
                {service.image && (
                  <div className="relative w-full aspect-[3/2] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                )}

                <div className="p-5 md:p-6">
                  {/* Icon */}
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-light flex items-center justify-center mb-3">
                    <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-brand-dark">
                      <i className={`${service.icon} text-lg md:text-xl`} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-base md:text-lg font-semibold text-brand-dark font-heading mb-2 leading-snug">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-brand-muted leading-relaxed mb-3">
                    {service.description}
                  </p>

                  {/* CTA */}
                  <span className="inline-flex items-center text-sm font-medium text-brand-mint group-hover:underline transition-all">
                    {t('services_learn_more')}
                    <i className="ri-arrow-right-line ml-1 text-sm" />
                  </span>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}